import { Pipe, PipeTransform } from '@angular/core';
import type { Timestamp } from '@marmon/shared/types/common';

const DefaultOptions = () => ({
	showNow: true,
	ago: true,
	exact: false,
});

/**
 * @todo add translation support
 */
@Pipe({
	name: 'relativeTime',
	standalone: true,
})
export class RelativeTimePipe implements PipeTransform {
	transform(
		value: Timestamp | Date,
		options?: { showNow?: boolean; ago?: boolean; exact?: boolean },
	) {
		const now = new Date();
		const { showNow, ago, exact } = { ...DefaultOptions(), ...options };

		let date = now;
		if (!(value instanceof Date) && value.toDate !== null) {
			date = value.toDate();
		} else if (value instanceof Date) date = value;

		let seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
		if (showNow && seconds < RelativeTime.secondsMinimum) return 'now';

		let interval = Math.floor(seconds / RelativeTime.secondsInYear);

		let displayValue: string[] = [];
		const display = (value: string) => (ago ? `${value} ago` : `${value}`);
		const addToDisplay = (value: string) => {
			if (!exact) displayValue = [value];
			else displayValue.push(value);
		};

		if (interval >= 1) {
			addToDisplay(display(interval + ` year${interval > 1 ? 's' : ''}`));
		}

		interval = Math.floor(seconds / RelativeTime.secondsInMonth);
		if (interval >= 1) {
			seconds -= RelativeTime.secondsInMonth * interval;
			addToDisplay(
				display(interval + ` month${interval > 1 ? 's' : ''}`),
			);
		}

		interval = Math.floor(seconds / RelativeTime.secondsInDay);
		if (interval >= 1) {
			seconds -= RelativeTime.secondsInDay * interval;
			addToDisplay(display(interval + ` day${interval > 1 ? 's' : ''}`));
		}

		interval = Math.floor(seconds / RelativeTime.secondsInHour);
		if (interval >= 1) {
			seconds -= RelativeTime.secondsInHour * interval;
			addToDisplay(display(interval + ` hour${interval > 1 ? 's' : ''}`));
		}

		interval = Math.floor(seconds / RelativeTime.secondsInMinute);
		if (interval >= 1) {
			seconds -= RelativeTime.secondsInMinute * interval;
			addToDisplay(
				display(interval + ` minute${interval > 1 ? 's' : ''}`),
			);
		}

		const rounded = Math.floor(seconds);
		addToDisplay(display(rounded + ` second${rounded !== 1 ? 's' : ''}`));
		return displayValue.join(' ');
	}
}

const RelativeTime = {
	secondsInYear: 31536000,
	secondsInMonth: 2592000,
	secondsInDay: 86400,
	secondsInHour: 3600,
	secondsInMinute: 60,
	secondsMinimum: 10,
};
